<template>
  <main class="home-page" :class="{ 'dark-mode': isDarkMode }">
    <div v-if="isProcessing" class="state-surface processing-indicator" role="status" aria-live="polite">
      <div class="spinner" aria-hidden="true">
        <span></span><span></span><span></span>
      </div>
    </div>

    <div v-else-if="isLoading" class="state-surface loading" role="status" aria-live="polite">
      <div class="spinner" aria-hidden="true">
        <span></span><span></span><span></span>
      </div>
      <span>Iboralar yuklanmoqda</span>
    </div>

    <div v-else class="layout-container">
      <section class="feed-panel" aria-labelledby="home-feed-title">
        <header class="feed-header">
          <div>
            <p class="section-kicker">Lug'at</p>
            <h1 id="home-feed-title">So'nggi iboralar</h1>
          </div>
          <span class="result-chip">{{ totalCount }} ta</span>
        </header>

        <div v-if="hasError" class="state-surface state-card error-state" role="alert">
          <i class="bi bi-exclamation-triangle" aria-hidden="true"></i>
          <div class="state-copy">
            <h2>Iboralarni yuklab bo'lmadi</h2>
            <p>Ulanishni tekshirib, qayta urinib ko'ring.</p>
          </div>
          <button type="button" class="filled-button" @click="fetchPhrases">
            Qayta urinish
          </button>
        </div>

        <div v-else-if="phrases.length" class="feed" aria-label="So'nggi iboralar">
          <DefinitionCard
            v-for="phrase in phrases"
            :key="phrase.slug"
            :phrase="phrase"
          />
        </div>

        <div v-else class="state-surface state-card empty-state">
          <i class="bi bi-inbox" aria-hidden="true"></i>
          <div class="state-copy">
            <h2>Ta'riflar topilmadi</h2>
            <p>Yangi iboralar qo'shilganda shu yerda ko'rinadi.</p>
          </div>
        </div>
      </section>

      <aside v-if="mostLikedPhrases.length" class="trending-panel" aria-labelledby="trending-title">
        <div class="trending-header">
          <p class="section-kicker">Trend</p>
          <h2 id="trending-title">Eng yoqqan iboralar</h2>
        </div>

        <ol class="trending-list">
          <li
            v-for="(phrase, index) in mostLikedPhrases"
            :key="phrase.slug"
            class="trending-item"
          >
            <button
              type="button"
              class="trending-link"
              @click="goToPhrase(phrase.slug)"
            >
              <span class="rank" aria-hidden="true">{{ index + 1 }}</span>
              <span class="trending-copy">
                <span class="trending-title">{{ phrase.title }}</span>
                <span class="trending-meta">
                  <span v-if="phrase.user">@{{ phrase.user }}</span>
                  <span>{{ phrase.total_likes }} like</span>
                </span>
              </span>
              <i class="bi bi-chevron-right" aria-hidden="true"></i>
            </button>
          </li>
        </ol>
      </aside>
    </div>

    <nav class="pagination" v-if="totalPages > 1" aria-label="Sahifalar">
      <button
        type="button"
        class="tonal-button"
        :disabled="currentPage === 1"
        @click="prevPage()"
        aria-label="Oldingi sahifa"
      >
        <i class="bi bi-chevron-left" aria-hidden="true"></i>
        Oldingi
      </button>
      <span class="page-indicator">{{ currentPage }} / {{ totalPages }}</span>
      <button
        type="button"
        class="tonal-button"
        :disabled="currentPage === totalPages"
        @click="nextPage()"
        aria-label="Keyingi sahifa"
      >
        Keyingi
        <i class="bi bi-chevron-right" aria-hidden="true"></i>
      </button>
    </nav>
  </main>
</template>

<script>
import axios from 'axios';
import DefinitionCard from '../components/DefinitionCard.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'HomePage',
  components: {
    DefinitionCard,
  },
  data() {
    return {
      mostLikedPhrases: [],
      phrases: [],
      isLoading: true,
      isProcessing: false,
      hasError: false,
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      itemsPerPage: 5,
    };
  },
  computed: {
    ...mapGetters(['isDarkMode']),
  },
  methods: {
    async fetchPhrases() {
      this.isLoading = true;
      this.hasError = false;
      try {
        const response = await axios.get(`phrases/phrases/?page=${this.currentPage}&page_size=${this.itemsPerPage}`);
        this.phrases = response.data.results || [];
        this.totalCount = response.data.count || 0;
        this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.itemsPerPage));
      } catch (error) {
        this.phrases = [];
        this.hasError = true;
      } finally {
        this.isLoading = false;
        this.$nextTick(() => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        });
      }
    },
    async fetchMostLikedPhrases() {
      try {
        const likedPhrasesResponse = await axios.get('phrases/liked-phrases/');
        this.mostLikedPhrases = likedPhrasesResponse.data || [];
      } catch (error) {
        this.mostLikedPhrases = [];
      }
    },
    goToPhrase(slug) {
      this.$router.push({ name: 'PhraseDetail', params: { slug } });
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
  },
  watch: {
    currentPage() {
      this.fetchPhrases();
    },
  },
  async mounted() {
    await Promise.all([this.fetchPhrases(), this.fetchMostLikedPhrases()]);
  },
};
</script>

<style scoped>
.home-page {
  min-height: 100vh;
  padding: clamp(16px, 3vw, 32px);
  color: var(--md-sys-color-on-background);
  background: var(--md-sys-color-background);
}

.layout-container {
  display: grid;
  grid-template-columns: minmax(0, 720px) minmax(260px, 320px);
  gap: 24px;
  align-items: start;
  width: min(1088px, 100%);
  margin: 0 auto;
}

.feed-panel {
  min-width: 0;
}

.feed-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.section-kicker {
  margin: 0 0 4px;
  color: var(--md-sys-color-primary);
  font: var(--md-sys-typescale-label-medium);
  letter-spacing: 0;
  text-transform: uppercase;
}

.feed-header h1,
.trending-header h2 {
  margin: 0;
  color: var(--md-sys-color-on-surface);
}

.feed-header h1 {
  font: var(--md-sys-typescale-headline-large);
}

.result-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding: 0 12px;
  border-radius: var(--md-sys-shape-corner-small);
  color: var(--md-sys-color-on-secondary-container);
  background: var(--md-sys-color-secondary-container);
  font: var(--md-sys-typescale-label-large);
  white-space: nowrap;
}

.feed {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.trending-panel,
.state-surface {
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: var(--md-sys-shape-corner-large);
  background: var(--md-sys-color-surface-container-low);
  box-shadow: var(--md-sys-elevation-1);
}

.trending-panel {
  position: sticky;
  top: 88px;
  padding: 16px 0 8px;
}

.trending-header {
  padding: 0 16px 8px;
}

.trending-header h2 {
  font: var(--md-sys-typescale-title-medium);
}

.trending-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0;
  margin: 0;
  list-style: none;
}

.trending-link {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) 20px;
  gap: 12px;
  align-items: center;
  width: 100%;
  min-height: 64px;
  padding: 8px 12px;
  border: 0;
  border-radius: 0;
  color: var(--md-sys-color-on-surface);
  background: transparent;
  text-align: left;
  cursor: pointer;
  font: inherit;
  transition: background-color var(--md-sys-motion-standard);
}

.trending-link:hover,
.trending-link:focus-visible {
  background: color-mix(in srgb, var(--md-sys-color-primary) 8%, transparent);
}

.trending-link:focus-visible,
.filled-button:focus-visible,
.tonal-button:focus-visible {
  outline: 2px solid var(--md-sys-color-primary);
  outline-offset: 2px;
}

.rank {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--md-sys-shape-corner-full);
  color: var(--md-sys-color-on-primary-container);
  background: var(--md-sys-color-primary-container);
  font: var(--md-sys-typescale-label-large);
}

.trending-copy {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.trending-title {
  overflow: hidden;
  color: var(--md-sys-color-on-surface);
  font: var(--md-sys-typescale-body-medium);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trending-meta {
  display: flex;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
  color: var(--md-sys-color-on-surface-variant);
  font: var(--md-sys-typescale-body-small);
  white-space: nowrap;
}

.state-surface {
  padding: 24px;
}

.loading,
.processing-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: min(640px, calc(100vw - 32px));
  margin: 24px auto;
  color: var(--md-sys-color-on-surface-variant);
  font: var(--md-sys-typescale-body-medium);
}

.state-card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 12px 16px;
  align-items: center;
}

.state-card i {
  color: var(--md-sys-color-primary);
  font-size: 1.75rem;
}

.state-copy h2 {
  margin: 0 0 4px;
  color: var(--md-sys-color-on-surface);
  font: var(--md-sys-typescale-title-medium);
}

.state-copy p {
  margin: 0;
  color: var(--md-sys-color-on-surface-variant);
  font: var(--md-sys-typescale-body-medium);
}

.error-state i,
.error-state h2 {
  color: var(--md-sys-color-error);
}

.filled-button,
.tonal-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 18px;
  border: 0;
  border-radius: var(--md-sys-shape-corner-full);
  cursor: pointer;
  font: var(--md-sys-typescale-label-large);
  transition: background-color var(--md-sys-motion-standard), opacity var(--md-sys-motion-standard);
}

.filled-button {
  grid-column: 2;
  width: fit-content;
  color: var(--md-sys-color-on-primary);
  background: var(--md-sys-color-primary);
}

.tonal-button {
  color: var(--md-sys-color-on-primary-container);
  background: var(--md-sys-color-primary-container);
}

.filled-button:hover,
.tonal-button:hover:not(:disabled) {
  background: color-mix(in srgb, currentColor 8%, var(--md-sys-color-primary-container));
}

.tonal-button:disabled {
  color: color-mix(in srgb, var(--md-sys-color-on-surface) 38%, transparent);
  background: color-mix(in srgb, var(--md-sys-color-on-surface) 12%, transparent);
  cursor: not-allowed;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: min(720px, 100%);
  margin: 24px auto 0;
}

.page-indicator {
  min-width: 72px;
  color: var(--md-sys-color-on-surface-variant);
  font: var(--md-sys-typescale-label-large);
  text-align: center;
}

.spinner span {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin: 0 4px;
  border-radius: 50%;
  background: var(--md-sys-color-primary);
  animation: pulse 1.2s infinite ease-in-out;
}

.spinner span:nth-child(2) {
  animation-delay: 0.2s;
}

.spinner span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.5;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}

@media (max-width: 960px) {
  .layout-container {
    grid-template-columns: minmax(0, 1fr);
  }

  .trending-panel {
    position: static;
    width: 100%;
  }
}

@media (max-width: 640px) {
  .home-page {
    padding: 12px;
  }

  .feed-header,
  .pagination {
    align-items: stretch;
    flex-direction: column;
  }

  .result-chip,
  .tonal-button {
    width: 100%;
  }

  .state-card {
    grid-template-columns: 1fr;
  }

  .filled-button {
    grid-column: 1;
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .spinner span {
    animation: none;
  }

  .trending-link,
  .filled-button,
  .tonal-button {
    transition: none;
  }
}
</style>
